import { styled } from '@mui/material/styles';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from '@mui/material';
import { useField } from 'formik';
import {
  getWCAGAutoCompleteValue,
  sortOptionsInAlphabeticOrder,
} from '../../utils/utilityFunctions';

export const BootstrapSelect = styled(Select)(({ theme }) => ({
  '&.MuiInputBase-root': {
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
    },
    '& .MuiSelect-select': {
      border: '1px solid #DDEAF3',
      fontSize: 16,
      padding: '10px 12px',
      borderRadius: 4,
    },
    '&.Mui-focusVisible .MuiSelect-select': {
      border: '2px solid #2057A6',
      padding: '9px 11px',
      borderRadius: '4px',
    },
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#DDEAF3',
  },
  '&.Mui-focused,&:hover': {
    '.MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: theme.palette.primary.main,
    },
  },
  svg: {
    color: '#337FC9',
  },
}));

export default function MFSelectField({
  name,
  label,
  items,
  onChange,
  disabled = false,
  indexing = false,
  applyLabelStyles = false,
  isSortingApplicable = true,
  placeholder, // FIX: Destructure placeholder out of props to block parameter leaks to hidden inputs
  ...rest
}: SelectProps & {
  name: string;
  label?: string;
  items: { key: string | number; value: string | number }[];
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  disabled?: boolean;
  indexing?: boolean;
  applyLabelStyles?: boolean;
  isSortingApplicable?: boolean;
}): JSX.Element {
  const [field, meta] = useField(name);
  onChange = onChange || field.onChange;
  const errorText = meta.error && meta.touched ? meta.error : '';

  const selectId = `${name}-select`;
  const labelId = `${name}-label`;
  const errorId = `${name}-error`;

  const autoCompleteToken = getWCAGAutoCompleteValue(name || '');

  /* FIX: Detects if the incoming string contains an asterisk element */
  const containsAsterisk = label ? label.includes('*') : false;
  // Separate styling configuration maps safely from properties
  const { sx: restSx, ...cleanRest } = rest;

  return (
    <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 2 } }}>
      {label && (
        <InputLabel
          id={labelId}
          htmlFor={selectId}
          shrink
          sx={{
            transform: 'unset',
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
            whiteSpace: applyLabelStyles ? 'normal' : 'nowrap',
            position: applyLabelStyles ? 'relative' : 'absolute',
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'unset' },
          }}>
          {label}
        </InputLabel>
      )}
      <BootstrapSelect
        id={selectId}
        labelId={labelId}
        aria-invalid={!!errorText}
        // FIX: Solved syntax structure errors by correctly merging conditional properties into inputProps
        inputProps={{
          'aria-labelledby': labelId,
          id: selectId,
          ...(autoCompleteToken ? { autoComplete: autoCompleteToken } : {}),
          ...(containsAsterisk && {
            'aria-label': `${label?.replace('*', '').trim()} required`,
            'aria-required': true,
          }),
        }}
        label={label}
        sx={{
          mt: applyLabelStyles ? '10px' : '32px',
          ...restSx,
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
        }}
        MenuProps={{
          sx: {
            '.MuiPaper-root ': {
              maxWidth: { xs: '100%', sm: '31%', md: '26%' },
              maxHeight: '20%',
              overflowX: 'auto',
            },
          },

          disableAutoFocusItem: false,
          MenuListProps: {
            disableListWrap: true,
          },
        }}
        {...field}
        {...cleanRest}
        onChange={onChange}
        disabled={disabled}>
        {(isSortingApplicable ? sortOptionsInAlphabeticOrder(items || []) : items || []).map(
          (item, index: number) => (
            <MenuItem
              key={item.key}
              value={item.value}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: 'common.black',
                '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
              }}>
              {indexing ? (
                <>
                  <Typography
                    component="span"
                    sx={{ color: 'primary.main', fontWeight: 500, fontSize: 14 }}>
                    C{index < 9 ? '0' + (index + 1) : index + 1}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'common.black',
                      '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
                    }}>
                    {item.key}
                  </Typography>
                </>
              ) : (
                <Typography component="span" sx={{ whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' } }}>
                  {item.key}
                </Typography>
              )}
            </MenuItem>
          )
        )}
      </BootstrapSelect>
      {errorText && (
        <FormHelperText
          error
          id={errorId}
          role="alert"
          aria-live="assertive"
          sx={{ marginLeft: 'unset' }}>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
